import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { bulkBinAPI, storeInventoryAPI, storeProductsAPI, posAPI } from '../../services/storeAPI';
import StoreHeader from '../../components/Layout/StoreHeader';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';

type ScanMode = 'bin' | 'inventory' | 'upc';

interface BinInfo {
  id: number;
  name: string;
  product_id: number | null;
  product_name: string;
  current_lbs: number;
  capacity_lbs: number;
  percentage_full: number;
  needs_refill: boolean;
}

interface ProductInfo {
  id: number;
  name: string;
  sku: string;
  upc: string | null;
  barcode: string | null;
  retail_price: string;
  effective_price?: string;
  category_name?: string;
  category_display?: string;
  size_lbs?: string;
}

interface InventoryInfo {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  quantity: number;
  location: string;
  location_display: string;
  reorder_threshold: number;
  needs_reorder: boolean;
  last_counted: string | null;
}

interface CountedItem {
  product: ProductInfo;
  inventory: InventoryInfo | null;
  countedQty: number;
  previousQty: number;
  discrepancy: number;
}

// Detect code type from scanned value
function detectCodeType(code: string): 'bin' | 'upc' | 'sku' | 'unknown' {
  if (code.startsWith('BIN-')) return 'bin';
  if (/^\d{8}$|^\d{12,14}$/.test(code)) return 'upc';
  if (code.startsWith('FST-') || code.startsWith('SKU-')) return 'sku';
  return 'unknown';
}

// Play a short beep on successful scan
function playBeep() {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.frequency.value = 1000;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audioCtx.close();
    }, 150);
  } catch {
    // Audio not supported
  }
}

const StoreScan: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<ScanMode>('bin');
  const [scanInput, setScanInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Camera state
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastScannedRef = useRef('');
  const lastScannedTimeRef = useRef(0);
  const wakeLockRef = useRef<any>(null);
  const scanHandlerRef = useRef<(code: string) => void>(() => {});
  // A code queued to auto-run right after a programmatic mode switch (the
  // Count <-> UPC Setup handoff). Consumed by the mode-reset effect, so its
  // results aren't clobbered by that reset — replaces the old setTimeout hack.
  const pendingScanRef = useRef<{ mode: ScanMode; code: string } | null>(null);

  // Bin Lookup state
  const [binInfo, setBinInfo] = useState<BinInfo | null>(null);
  const [showFillModal, setShowFillModal] = useState(false);

  // Inventory Count state
  const [countProduct, setCountProduct] = useState<ProductInfo | null>(null);
  const [countInventory, setCountInventory] = useState<InventoryInfo | null>(null);
  const [countValue, setCountValue] = useState('');
  const [countNotes, setCountNotes] = useState('');
  const [countedItems, setCountedItems] = useState<CountedItem[]>([]);

  // UPC Setup state
  const [upcProduct, setUpcProduct] = useState<ProductInfo | null>(null);
  const [upcNotFound, setUpcNotFound] = useState(false);
  const [upcScannedCode, setUpcScannedCode] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState<ProductInfo[]>([]);
  const [allProducts, setAllProducts] = useState<ProductInfo[]>([]);
  // Set when the user reaches UPC Setup from a "not found" in Inventory Count;
  // a successful link of this exact code bounces back to Count and re-scans it.
  const [returnToCountCode, setReturnToCountCode] = useState('');

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modeHint, setModeHint] = useState('');

  // ---- CAMERA MANAGEMENT ----
  const stopCamera = useCallback(() => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setHasFlash(false);
    setFlashOn(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');

    // Check camera permission
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      if (result.state === 'denied') {
        setCameraError('Camera permission denied. Please enable camera in browser settings, or enter codes manually below.');
        return;
      }
    } catch {
      // permissions.query not supported — try anyway
    }

    try {
      // Stop any existing camera first
      stopCamera();

      const codeReader = new BrowserMultiFormatReader();
      codeReaderRef.current = codeReader;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } }
      });
      streamRef.current = stream;

      // Check flash capability
      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities?.() as any;
      if (capabilities?.torch) {
        setHasFlash(true);
      }

      // Start decoding from the video element
      await codeReader.decodeFromVideoDevice(
        null,
        videoRef.current!,
        (result, err) => {
          if (result) {
            const code = result.getText();
            const now = Date.now();

            // Debounce: ignore same code within 2s
            if (code === lastScannedRef.current && now - lastScannedTimeRef.current < 2000) {
              return;
            }
            lastScannedRef.current = code;
            lastScannedTimeRef.current = now;

            // Feedback
            playBeep();
            if (navigator.vibrate) navigator.vibrate(150);

            scanHandlerRef.current(code);
          }
          if (err && !(err instanceof NotFoundException)) {
            console.error('Scan error:', err);
          }
        }
      );

      setCameraActive(true);
    } catch (err: any) {
      console.error('Camera error:', err);
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access is needed for scanning. Please allow camera access in your browser settings, or enter codes manually below.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera detected. Use the manual input below to enter barcodes.');
      } else if (err.name === 'NotReadableError') {
        setCameraError('Camera is in use by another app. Close other camera apps and try again.');
      } else {
        setCameraError('Failed to access camera. Use the manual input below.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopCamera]);

  const toggleFlash = async () => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    try {
      await track.applyConstraints({ advanced: [{ torch: !flashOn } as any] });
      setFlashOn(!flashOn);
    } catch {
      // Flash not supported on this device
    }
  };

  // ---- WAKE LOCK ----
  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      } catch {
        // Wake lock failed (e.g., low battery)
      }
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  // ---- SCAN HANDLER (called by camera or manual submit) ----
  const handleScannedCode = useCallback((code: string) => {
    const codeType = detectCodeType(code);
    setModeHint('');

    switch (mode) {
      case 'bin':
        if (codeType === 'bin' || codeType === 'unknown') {
          handleBinScan(code);
        } else {
          // UPC/SKU scanned in bin mode — suggest switch
          setModeHint('upc_in_bin');
        }
        break;

      case 'inventory':
        if (codeType === 'upc' || codeType === 'sku' || codeType === 'unknown') {
          handleInventoryScan(code);
        } else if (codeType === 'bin') {
          setModeHint('bin_in_product');
        }
        break;

      case 'upc':
        if (codeType === 'upc' || codeType === 'sku' || codeType === 'unknown') {
          handleUpcScan(code);
        } else if (codeType === 'bin') {
          setModeHint('bin_in_product');
        }
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Keep the ref in sync so the camera callback always uses latest handler
  useEffect(() => {
    scanHandlerRef.current = handleScannedCode;
  }, [handleScannedCode]);

  // ---- LIFECYCLE ----
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    // Auto-start camera on mount
    startCamera();
    requestWakeLock();

    return () => {
      stopCamera();
      releaseWakeLock();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Reset state when mode changes, keep camera running
    setScanInput('');
    setError('');
    setSuccess('');
    setModeHint('');
    setBinInfo(null);
    setCountProduct(null);
    setCountInventory(null);
    setCountValue('');
    setCountNotes('');
    setInventoryNotFound(false);
    setInventoryNotFoundCode('');
    setUpcProduct(null);
    setUpcNotFound(false);
    setUpcScannedCode('');
    setProductSearch('');
    setProductResults([]);
    lastScannedRef.current = '';

    // Handoff: if a code was queued for this mode, run it now — AFTER the reset
    // above, so its results survive. The handler's synchronous setState calls
    // win over the resets in this same tick.
    const pending = pendingScanRef.current;
    if (pending && pending.mode === mode) {
      pendingScanRef.current = null;
      if (mode === 'upc') handleUpcScan(pending.code);
      else if (mode === 'inventory') handleInventoryScan(pending.code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ---- BIN LOOKUP ----
  const handleBinScan = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setBinInfo(null);
    try {
      const data = await bulkBinAPI.getByQR(code.trim());
      setBinInfo(data);
      setScanInput(code.trim());
    } catch (err: any) {
      setError(err.message || 'Bin not found');
    } finally {
      setLoading(false);
    }
  };

  const handleFill = async (amountLbs: number, notes: string) => {
    if (!binInfo) return;
    try {
      await bulkBinAPI.fill(binInfo.id, amountLbs, notes);
      setShowFillModal(false);
      handleBinScan(scanInput);
    } catch (err: any) {
      alert(err.message || 'Failed to fill bin');
    }
  };

  // ---- INVENTORY COUNT ----
  const [inventoryNotFound, setInventoryNotFound] = useState(false);
  const [inventoryNotFoundCode, setInventoryNotFoundCode] = useState('');
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Fetch inventory whenever countProduct changes (reactive, not nested in async chain)
  useEffect(() => {
    if (!countProduct || mode !== 'inventory') return;
    let cancelled = false;

    const fetchInventory = async () => {
      setInventoryLoading(true);
      try {
        console.log('[Scan] Fetching inventory for product', countProduct.id, countProduct.name);
        const invData = await storeInventoryAPI.getByProduct(countProduct.id);
        if (!cancelled) {
          console.log('[Scan] Inventory response:', invData);
          setCountInventory(invData.inventory);
        }
      } catch (err) {
        console.error('[Scan] Inventory fetch failed:', err);
        // Auto-create should prevent this, but handle gracefully
        if (!cancelled) setCountInventory(null);
      } finally {
        if (!cancelled) setInventoryLoading(false);
      }
    };

    fetchInventory();

    // 5-second timeout fallback — show count form with qty 0
    const timeout = setTimeout(() => {
      if (!cancelled && !countInventory) {
        console.warn('[Scan] Inventory fetch timed out, showing empty form');
        setInventoryLoading(false);
        setCountInventory({
          id: 0,
          product_id: countProduct.id,
          product_name: countProduct.name,
          product_sku: countProduct.sku,
          quantity: 0,
          location: 'FLOOR',
          location_display: 'Floor',
          reorder_threshold: 0,
          needs_reorder: false,
          last_counted: null,
        });
      }
    }, 5000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countProduct, mode]);

  // Keep the manual entry field focused whenever no result card owns the screen
  // (and on mount), so a hardware wedge scanner — which types the code + Enter —
  // works continuously without a re-tap. When a count/search card is up, its own
  // autoFocus input owns focus and this stays out of the way.
  useEffect(() => {
    if (!binInfo && !countProduct && !inventoryNotFound && !upcProduct && !upcNotFound) {
      inputRef.current?.focus();
    }
  }, [mode, binInfo, countProduct, inventoryNotFound, upcProduct, upcNotFound]);

  const handleInventoryScan = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setCountProduct(null);
    setCountInventory(null);
    setCountValue('');
    setCountNotes('');
    setInventoryNotFound(false);
    setInventoryNotFoundCode('');
    setInventoryLoading(false);
    try {
      const response = await posAPI.lookupBarcode(code.trim());
      const product = response.product;
      console.log('[Scan] Product found:', product?.id, product?.name);
      setCountProduct(product);
      setScanInput(code.trim());
      // Inventory fetch is handled by the useEffect above (reacts to countProduct change)
    } catch {
      // Product not found — show helpful UI instead of error text
      setInventoryNotFound(true);
      setInventoryNotFoundCode(code.trim());
      setScanInput(code.trim());
    } finally {
      setLoading(false);
    }
  };

  const handleCountSubmit = async () => {
    if (!countProduct || !countInventory) return;
    const qty = parseInt(countValue, 10);
    if (isNaN(qty) || qty < 0) {
      setError('Enter a valid count');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // If inventory was from timeout fallback (id=0), fetch real record first
      let invId = countInventory.id;
      if (invId === 0) {
        const invData = await storeInventoryAPI.getByProduct(countProduct.id);
        if (invData.inventory) {
          invId = invData.inventory.id;
          setCountInventory(invData.inventory);
        }
      }
      await storeInventoryAPI.count(invId, qty, countNotes);
      const item: CountedItem = {
        product: countProduct,
        inventory: countInventory,
        countedQty: qty,
        previousQty: countInventory.quantity,
        discrepancy: qty - countInventory.quantity,
      };
      setCountedItems(prev => [item, ...prev]);
      setSuccess(`Counted ${countProduct.name}: ${qty} (was ${countInventory.quantity})`);
      setCountProduct(null);
      setCountInventory(null);
      setCountValue('');
      setCountNotes('');
      setScanInput('');
      lastScannedRef.current = '';
      // Camera stays active — ready for next scan
    } catch (err: any) {
      setError(err.message || 'Failed to save count');
    } finally {
      setLoading(false);
    }
  };

  // ---- UPC SETUP ----
  const handleUpcScan = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setUpcProduct(null);
    setUpcNotFound(false);
    setUpcScannedCode(code.trim());
    setScanInput(code.trim());
    try {
      const response = await posAPI.lookupBarcode(code.trim());
      setUpcProduct(response.product);
    } catch {
      setUpcNotFound(true);
      if (allProducts.length === 0) {
        try {
          const products = await storeProductsAPI.getAll();
          setAllProducts(products);
        } catch { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProductSearch = (query: string) => {
    setProductSearch(query);
    if (!query.trim()) {
      setProductResults([]);
      return;
    }
    const q = query.toLowerCase();
    const filtered = allProducts.filter(
      p => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
    ).slice(0, 10);
    setProductResults(filtered);
  };

  const handleLinkBarcode = async (productId: number) => {
    if (!upcScannedCode) return;
    setLoading(true);
    setError('');
    try {
      const result = await storeProductsAPI.linkBarcode(productId, upcScannedCode);
      setSuccess(`Barcode linked to ${result.product_name}`);
      setUpcNotFound(false);
      setProductSearch('');
      setProductResults([]);
      setScanInput('');
      const linkedCode = upcScannedCode;
      setUpcScannedCode('');
      lastScannedRef.current = '';
      // Handoff back: if we came from a "not found" in Inventory Count, return to
      // Count and re-scan the now-linked code so it can be counted (no dead-end).
      if (returnToCountCode && returnToCountCode === linkedCode) {
        setReturnToCountCode('');
        pendingScanRef.current = { mode: 'inventory', code: linkedCode };
        setMode('inventory');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to link barcode');
    } finally {
      setLoading(false);
    }
  };

  // ---- MANUAL SUBMIT ----
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleScannedCode(scanInput);
  };

  const getStatusColor = (percentage: number) => {
    if (percentage <= 20) return '#f44336';
    if (percentage <= 40) return '#ff9800';
    return '#4CAF50';
  };

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />

      <main style={styles.main}>
        {/* Mode Tabs */}
        <div style={styles.tabs}>
          {([
            { key: 'bin' as ScanMode, label: 'Bin Lookup' },
            { key: 'inventory' as ScanMode, label: 'Inventory Count' },
            { key: 'upc' as ScanMode, label: 'UPC Setup' },
          ]).map(tab => (
            <button
              key={tab.key}
              style={{
                ...styles.tab,
                ...(mode === tab.key ? styles.tabActive : {}),
              }}
              onClick={() => setMode(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Camera Section */}
        <div style={styles.cameraSection}>
          {cameraError ? (
            <div style={styles.cameraErrorBox}>
              <p style={styles.cameraErrorText}>{cameraError}</p>
              <button style={styles.retryBtn} onClick={startCamera}>
                Retry Camera
              </button>
            </div>
          ) : (
            <>
              <div style={styles.videoWrapper}>
                <video
                  ref={videoRef}
                  style={styles.video}
                  playsInline
                  autoPlay
                  muted
                />
                {/* Viewfinder overlay */}
                <div style={styles.viewfinder}>
                  <div style={styles.viewfinderCornerTL} />
                  <div style={styles.viewfinderCornerTR} />
                  <div style={styles.viewfinderCornerBL} />
                  <div style={styles.viewfinderCornerBR} />
                </div>
                {!cameraActive && !cameraError && (
                  <div style={styles.cameraLoading}>Starting camera...</div>
                )}
                {/* Flash toggle */}
                {hasFlash && (
                  <button
                    style={{
                      ...styles.flashBtn,
                      backgroundColor: flashOn ? '#FFC107' : 'rgba(255,255,255,0.25)',
                    }}
                    onClick={toggleFlash}
                  >
                    {flashOn ? 'Flash ON' : 'Flash OFF'}
                  </button>
                )}
              </div>
              <p style={styles.cameraHint}>
                {mode === 'bin' ? 'Point at bin QR code' :
                 mode === 'inventory' ? 'Point at product barcode' :
                 'Point at barcode/UPC'}
              </p>
            </>
          )}
        </div>

        {/* Mode hint (wrong code type) — tappable suggestions */}
        {modeHint === 'upc_in_bin' && (
          <div style={styles.modeHintBox}>
            <span>That's a product barcode.</span>
            <div style={styles.hintActions}>
              <button style={styles.hintBtn} onClick={() => setMode('inventory')}>
                Switch to Inventory Count
              </button>
              <button style={styles.hintBtn} onClick={() => setMode('upc')}>
                Switch to UPC Setup
              </button>
            </div>
          </div>
        )}
        {modeHint === 'bin_in_product' && (
          <div style={styles.modeHintBox}>
            <span>That's a bin QR code.</span>
            <div style={styles.hintActions}>
              <button style={styles.hintBtn} onClick={() => setMode('bin')}>
                Switch to Bin Lookup
              </button>
            </div>
          </div>
        )}

        {/* Manual Input Fallback */}
        <div style={styles.manualSection}>
          <p style={styles.orDivider}>— or enter manually —</p>
          <form onSubmit={handleSubmit} style={styles.form}>
            <input
              ref={inputRef}
              type="text"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder={
                mode === 'bin' ? 'Enter bin code...' :
                mode === 'inventory' ? 'Enter product barcode...' :
                'Enter barcode/UPC...'
              }
              style={styles.scanInput}
              autoComplete="off"
            />
            <button type="submit" style={styles.scanButton} disabled={loading}>
              {loading ? '...' : 'Lookup'}
            </button>
          </form>
        </div>

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {/* ---- BIN LOOKUP RESULTS ---- */}
        {mode === 'bin' && binInfo && (
          <div style={styles.card}>
            <div style={styles.binHeader}>
              <div>
                <h2 style={styles.cardTitle}>{binInfo.name}</h2>
                <p style={styles.cardSubtitle}>{binInfo.product_name}</p>
              </div>
              {binInfo.needs_refill && (
                <span style={styles.refillBadge}>NEEDS REFILL</span>
              )}
            </div>
            <div style={styles.levelSection}>
              <div style={styles.levelBar}>
                <div
                  style={{
                    ...styles.levelFill,
                    width: `${binInfo.percentage_full}%`,
                    backgroundColor: getStatusColor(binInfo.percentage_full),
                  }}
                />
              </div>
              <div style={styles.levelStats}>
                <span style={styles.percentageText}>
                  {binInfo.percentage_full.toFixed(0)}% Full
                </span>
                <span style={styles.lbsText}>
                  {binInfo.current_lbs.toFixed(0)} / {binInfo.capacity_lbs} lbs
                </span>
              </div>
            </div>
            <div style={styles.actionRow}>
              <button style={styles.primaryBtn} onClick={() => setShowFillModal(true)}>
                Fill This Bin
              </button>
              <button style={styles.secondaryBtn} onClick={() => navigate('/store/bins')}>
                View All Bins
              </button>
            </div>
          </div>
        )}

        {/* ---- INVENTORY COUNT RESULTS ---- */}
        {mode === 'inventory' && countProduct && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>{countProduct.name}</h2>
            <p style={styles.cardSubtitle}>
              SKU: {countProduct.sku}
              {countProduct.category_name ? ` \u00B7 ${countProduct.category_name}` : ''}
            </p>
            {countInventory ? (
              <>
                <div style={styles.infoBox}>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Current Stock:</span>
                    <span style={{
                      ...styles.infoValue,
                      color: countInventory.needs_reorder ? '#f44336' : '#333',
                    }}>
                      {countInventory.quantity} bags
                      {countInventory.needs_reorder && ' (LOW)'}
                      {countInventory.quantity === 0 && !countInventory.last_counted && (
                        <span style={{ color: '#999', fontWeight: 400, fontSize: '0.8rem' }}> (new — never counted)</span>
                      )}
                    </span>
                  </div>
                  <div style={styles.infoRow}>
                    <span style={styles.infoLabel}>Location:</span>
                    <span style={styles.infoValue}>{countInventory.location_display}</span>
                  </div>
                  {countInventory.last_counted && (
                    <div style={styles.infoRow}>
                      <span style={styles.infoLabel}>Last Counted:</span>
                      <span style={styles.infoValue}>
                        {new Date(countInventory.last_counted).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Actual Count</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={countValue}
                    onChange={(e) => setCountValue(e.target.value)}
                    style={styles.input}
                    placeholder="Enter actual count..."
                    autoFocus
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes (optional)</label>
                  <input
                    type="text"
                    value={countNotes}
                    onChange={(e) => setCountNotes(e.target.value)}
                    style={styles.input}
                    placeholder="Any notes..."
                  />
                </div>
                {countValue && !isNaN(parseInt(countValue, 10)) && (
                  <div style={{
                    ...styles.discrepancy,
                    backgroundColor: parseInt(countValue, 10) !== countInventory.quantity ? '#fff3e0' : '#e8f5e9',
                  }}>
                    Discrepancy: {parseInt(countValue, 10) - countInventory.quantity}
                  </div>
                )}
                <button
                  style={styles.primaryBtn}
                  onClick={handleCountSubmit}
                  disabled={loading || !countValue}
                >
                  {loading ? 'Saving...' : 'Save & Next'}
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                {inventoryLoading ? 'Loading inventory...' : 'Preparing count form...'}
              </div>
            )}
          </div>
        )}

        {/* Inventory Count — product not found */}
        {mode === 'inventory' && inventoryNotFound && (
          <div style={styles.card}>
            <div style={styles.warningBox}>
              Product not found for UPC <strong>{inventoryNotFoundCode}</strong>
            </div>
            <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 0.75rem 0' }}>
              This barcode isn't linked to any product yet.
            </p>
            <div style={styles.actionRow}>
              <button
                style={styles.primaryBtn}
                onClick={() => {
                  // Remember where we came from, queue the code for UPC Setup, and
                  // switch — the mode-reset effect runs handleUpcScan after resetting.
                  setReturnToCountCode(inventoryNotFoundCode);
                  pendingScanRef.current = { mode: 'upc', code: inventoryNotFoundCode };
                  setMode('upc');
                }}
              >
                Switch to UPC Setup to link it
              </button>
              <button
                style={styles.secondaryBtn}
                onClick={() => {
                  setInventoryNotFound(false);
                  setInventoryNotFoundCode('');
                  setScanInput('');
                  lastScannedRef.current = '';
                }}
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Counted Items Summary */}
        {mode === 'inventory' && countedItems.length > 0 && !countProduct && !inventoryNotFound && (
          <div style={styles.card}>
            <h3 style={{ margin: '0 0 0.75rem 0', color: '#333' }}>
              Counted Items ({countedItems.length})
            </h3>
            {countedItems.map((item, i) => (
              <div key={i} style={styles.summaryRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ fontSize: '0.9rem' }}>{item.product.name}</strong>
                  <span style={styles.summaryDetail}>
                    {' '}Was {item.previousQty} → Now {item.countedQty}
                  </span>
                </div>
                <span style={{
                  color: item.discrepancy === 0 ? '#4CAF50' :
                         item.discrepancy > 0 ? '#2196F3' : '#f44336',
                  fontWeight: 600,
                  flexShrink: 0,
                }}>
                  {item.discrepancy > 0 ? '+' : ''}{item.discrepancy}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ---- UPC SETUP RESULTS ---- */}
        {mode === 'upc' && upcProduct && (
          <div style={styles.card}>
            <div style={{ ...styles.successInline, marginBottom: '0.75rem' }}>
              Barcode is already linked
            </div>
            <h2 style={styles.cardTitle}>{upcProduct.name}</h2>
            <div style={styles.infoBox}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>SKU:</span>
                <span style={styles.infoValue}>{upcProduct.sku}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>UPC:</span>
                <span style={styles.infoValue}>{upcProduct.upc || upcProduct.barcode || 'N/A'}</span>
              </div>
              {upcProduct.category_name && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Category:</span>
                  <span style={styles.infoValue}>{upcProduct.category_name}</span>
                </div>
              )}
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Price:</span>
                <span style={styles.infoValue}>${upcProduct.effective_price || upcProduct.retail_price}</span>
              </div>
            </div>
            <button
              style={styles.primaryBtn}
              onClick={() => {
                setUpcProduct(null);
                setUpcScannedCode('');
                setScanInput('');
                lastScannedRef.current = '';
              }}
            >
              Scan Next
            </button>
          </div>
        )}

        {mode === 'upc' && upcNotFound && (
          <div style={styles.card}>
            <div style={styles.warningBox}>
              Barcode <strong>{upcScannedCode}</strong> is not linked to any product
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Search for a product to link</label>
              <input
                type="text"
                value={productSearch}
                onChange={(e) => handleProductSearch(e.target.value)}
                style={styles.input}
                placeholder="Search by name or SKU..."
                autoFocus
              />
            </div>
            {productResults.length > 0 && (
              <div style={styles.productList}>
                {productResults.map(p => (
                  <div key={p.id} style={styles.productItem}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong>{p.name}</strong>
                      <span style={styles.productSku}> ({p.sku})</span>
                    </div>
                    <button
                      style={styles.linkBtn}
                      onClick={() => handleLinkBarcode(p.id)}
                      disabled={loading}
                    >
                      Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ready to Scan — only when no results are showing */}
        {!loading && !error && !binInfo && !countProduct && !inventoryNotFound && !upcProduct && !upcNotFound && countedItems.length === 0 && !modeHint && (
          <div style={styles.readyCard}>
            <h3 style={styles.readyTitle}>
              {cameraActive ? 'Scanning...' : 'Ready to Scan'}
            </h3>
            <p style={styles.readyText}>
              {mode === 'bin' && 'Point the camera at a bin QR code, or type the code manually above.'}
              {mode === 'inventory' && 'Point the camera at a product barcode to look up stock and record a count.'}
              {mode === 'upc' && 'Point the camera at a barcode to check if it\'s linked to a product, or link it.'}
            </p>
          </div>
        )}
      </main>

      {/* Fill Modal */}
      {showFillModal && binInfo && (
        <FillModal
          bin={binInfo}
          onClose={() => setShowFillModal(false)}
          onSubmit={handleFill}
        />
      )}
    </div>
  );
};

// Fill Modal
const FillModal: React.FC<{
  bin: BinInfo;
  onClose: () => void;
  onSubmit: (amountLbs: number, notes: string) => void;
}> = ({ bin, onClose, onSubmit }) => {
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState('');

  const maxFill = bin.capacity_lbs - bin.current_lbs;
  const newLevel = Math.min(bin.current_lbs + amount, bin.capacity_lbs);
  const newPercentage = (newLevel / bin.capacity_lbs) * 100;

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h2 style={styles.modalTitle}>Fill Bin</h2>
        <p style={styles.modalSubtitle}>{bin.name} - {bin.product_name}</p>

        <div style={styles.currentLevel}>
          Current: {bin.current_lbs.toFixed(0)} / {bin.capacity_lbs} lbs ({bin.percentage_full.toFixed(0)}%)
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Amount to Add (lbs)</label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value as any)}
            onBlur={(e) => {
              const val = parseFloat(e.target.value) || 0;
              setAmount(Math.max(0, Math.min(val, maxFill)));
            }}
            style={styles.input}
            autoFocus
          />
          <p style={styles.helperText}>Max: {maxFill.toFixed(0)} lbs</p>
          {amount > 0 && (
            <p style={styles.newLevel}>
              New Level: {newLevel.toFixed(0)} lbs ({newPercentage.toFixed(0)}%)
            </p>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={styles.textarea}
            placeholder="Any notes about this fill..."
          />
        </div>

        <div style={styles.modalActions}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={styles.submitBtn}
            onClick={() => onSubmit(amount, notes)}
            disabled={amount <= 0}
          >
            Confirm Fill
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    overflowX: 'hidden',
  },
  main: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: 'clamp(0.75rem, 3vw, 1.5rem)',
    width: '100%',
    boxSizing: 'border-box',
  },
  // Tabs
  tabs: {
    display: 'flex',
    gap: '0.25rem',
    marginBottom: '0.75rem',
    width: '100%',
  },
  tab: {
    flex: 1,
    padding: '0.625rem 0.25rem',
    border: '2px solid #e0e0e0',
    backgroundColor: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)',
    fontWeight: 600,
    color: '#666',
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
  },
  tabActive: {
    borderColor: '#1a4d1a',
    backgroundColor: '#1a4d1a',
    color: '#fff',
  },
  // Camera
  cameraSection: {
    backgroundColor: '#000',
    borderRadius: '12px',
    overflow: 'hidden',
    marginBottom: '0.75rem',
  },
  videoWrapper: {
    position: 'relative' as const,
    width: '100%',
    aspectRatio: '4/3',
    backgroundColor: '#111',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  },
  viewfinder: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75%',
    height: '35%',
    border: '2px solid rgba(255,255,255,0.6)',
    borderRadius: '8px',
    pointerEvents: 'none' as const,
  },
  viewfinderCornerTL: {
    position: 'absolute' as const,
    top: '-2px',
    left: '-2px',
    width: '20px',
    height: '20px',
    borderTop: '4px solid #4CAF50',
    borderLeft: '4px solid #4CAF50',
    borderTopLeftRadius: '4px',
  },
  viewfinderCornerTR: {
    position: 'absolute' as const,
    top: '-2px',
    right: '-2px',
    width: '20px',
    height: '20px',
    borderTop: '4px solid #4CAF50',
    borderRight: '4px solid #4CAF50',
    borderTopRightRadius: '4px',
  },
  viewfinderCornerBL: {
    position: 'absolute' as const,
    bottom: '-2px',
    left: '-2px',
    width: '20px',
    height: '20px',
    borderBottom: '4px solid #4CAF50',
    borderLeft: '4px solid #4CAF50',
    borderBottomLeftRadius: '4px',
  },
  viewfinderCornerBR: {
    position: 'absolute' as const,
    bottom: '-2px',
    right: '-2px',
    width: '20px',
    height: '20px',
    borderBottom: '4px solid #4CAF50',
    borderRight: '4px solid #4CAF50',
    borderBottomRightRadius: '4px',
  },
  cameraLoading: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#fff',
    fontSize: '1rem',
  },
  flashBtn: {
    position: 'absolute' as const,
    top: '10px',
    right: '10px',
    padding: '6px 14px',
    borderRadius: '20px',
    border: 'none',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    color: '#fff',
    zIndex: 2,
  },
  cameraHint: {
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center' as const,
    fontSize: '0.8rem',
    padding: '0.5rem 0',
    margin: 0,
  },
  cameraErrorBox: {
    padding: '1.5rem',
    textAlign: 'center' as const,
  },
  cameraErrorText: {
    color: '#ff8a80',
    fontSize: '0.9rem',
    lineHeight: 1.5,
    margin: '0 0 1rem 0',
  },
  retryBtn: {
    padding: '0.5rem 1.5rem',
    backgroundColor: '#fff',
    color: '#333',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  modeHintBox: {
    padding: '0.75rem',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    textAlign: 'center' as const,
    fontSize: '0.875rem',
  },
  hintActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    marginTop: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  hintBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1565c0',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  // Manual input
  manualSection: {
    marginBottom: '0.75rem',
  },
  orDivider: {
    textAlign: 'center' as const,
    color: '#999',
    fontSize: '0.8rem',
    margin: '0 0 0.5rem 0',
  },
  form: {
    display: 'flex',
    gap: '0.5rem',
    width: '100%',
  },
  scanInput: {
    flex: 1,
    minWidth: 0,
    padding: '0.75rem',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '44px',
    boxSizing: 'border-box' as const,
  },
  scanButton: {
    flexShrink: 0,
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1.25rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    minHeight: '44px',
  },
  // Feedback
  error: {
    padding: '0.75rem',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '8px',
    textAlign: 'center' as const,
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
  },
  success: {
    padding: '0.75rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '8px',
    textAlign: 'center' as const,
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
  },
  successInline: {
    padding: '0.75rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '8px',
    textAlign: 'center' as const,
    fontSize: '0.9rem',
  },
  warningBox: {
    padding: '0.75rem',
    backgroundColor: '#fff3e0',
    color: '#e65100',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
  },
  // Cards
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: 'clamp(1rem, 3vw, 1.5rem)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: '0.75rem',
  },
  cardTitle: {
    fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  },
  cardSubtitle: {
    color: '#666',
    marginTop: '0.25rem',
    fontSize: '0.9rem',
  },
  binHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    gap: '0.5rem',
  },
  refillBadge: {
    padding: '0.375rem 0.75rem',
    backgroundColor: '#f44336',
    color: '#fff',
    borderRadius: '6px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  levelSection: {
    marginBottom: '1rem',
  },
  levelBar: {
    height: '32px',
    backgroundColor: '#e0e0e0',
    borderRadius: '16px',
    overflow: 'hidden',
    marginBottom: '0.5rem',
  },
  levelFill: {
    height: '100%',
    transition: 'width 0.3s ease',
  },
  levelStats: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  percentageText: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#333',
  },
  lbsText: {
    fontSize: '0.9rem',
    color: '#666',
  },
  actionRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  primaryBtn: {
    flex: 1,
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
  },
  secondaryBtn: {
    flex: 1,
    backgroundColor: '#fff',
    color: '#1a4d1a',
    border: '2px solid #1a4d1a',
    padding: '0.875rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
  },
  // Inventory count
  infoBox: {
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '0.75rem',
    marginBottom: '0.75rem',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.25rem 0',
  },
  infoLabel: {
    color: '#666',
    fontSize: '0.875rem',
  },
  infoValue: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.875rem',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.625rem 0',
    borderBottom: '1px solid #f0f0f0',
    gap: '0.5rem',
  },
  summaryDetail: {
    color: '#888',
    fontSize: '0.8rem',
  },
  discrepancy: {
    padding: '0.625rem',
    borderRadius: '8px',
    textAlign: 'center' as const,
    fontWeight: 600,
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
  },
  // UPC Setup
  productList: {
    maxHeight: '250px',
    overflowY: 'auto' as const,
  },
  productItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.625rem',
    borderBottom: '1px solid #f0f0f0',
    gap: '0.5rem',
  },
  productSku: {
    color: '#888',
    fontSize: '0.8rem',
  },
  linkBtn: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  // Ready state
  readyCard: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '2rem 1.5rem',
    textAlign: 'center' as const,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  readyTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.5rem 0',
  },
  readyText: {
    color: '#666',
    lineHeight: 1.5,
    margin: 0,
    fontSize: '0.9rem',
  },
  // Form elements
  formGroup: {
    marginBottom: '0.75rem',
  },
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.25rem',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box' as const,
    minHeight: '44px',
  },
  helperText: {
    fontSize: '0.75rem',
    color: '#888',
    marginTop: '0.25rem',
  },
  newLevel: {
    fontSize: '0.85rem',
    color: '#1a4d1a',
    fontWeight: 500,
    marginTop: '0.375rem',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    minHeight: '80px',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  // Modal
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 0.25rem 0',
  },
  modalSubtitle: {
    color: '#666',
    margin: '0 0 1rem 0',
  },
  currentLevel: {
    padding: '0.75rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  },
  modalActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.875rem',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  submitBtn: {
    flex: 1,
    padding: '0.875rem',
    border: 'none',
    backgroundColor: '#4CAF50',
    color: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
  },
};

export default StoreScan;
